import { c as _c } from "react/compiler-runtime";
import { MutationOptions, useMutation } from '@tanstack/react-query';
import { Overwrite } from 'utility-types';
import { indexOf } from 'lodash';
import { GroupRuleOperator as GroupRuleOperatorEnum, type GroupDynamicMembershipProps, type GroupProps as OptionalGroupProps } from '@commutifi/models/Groups';
import { WorksiteProps } from '@commutifi/models/Worksites';
import { Account } from '@commutifi-fe/interfaces';
import { PlanProps } from '@commutifi/models/Plan';
import { GetResponse, RequestParams } from 'shared/interfaces';
import { JSON_HEADER, apiCall, dashboardApiServer } from '../../index';
import { captureIsPlanValid } from '../../utils/global';
import endpoints from './endpoints';
export enum RuleType {
  LOCATION = 'location',
  ATTRIBUTE = 'attribute',
}
export const GroupRuleOperatorValue = {
  Is: GroupRuleOperatorEnum.Is,
  IsNot: GroupRuleOperatorEnum.IsNot
} as const;

// The upstream @commutifi/models enum narrows to its literal members when used as a type
// (e.g. operator fields are typed as `${GroupRuleOperator}`). Re-export the string-literal
// union so our callers can pass raw "is"/"is_not" string values without enum-member checks.
export type GroupRuleOperator = `${GroupRuleOperatorEnum}`;
export const apiDashboardServer = apiCall(dashboardApiServer);
export type GroupDynamicMembershipWithRuleType = GroupDynamicMembershipProps & {
  ruleType?: `${RuleType}`;
};

/** Write payload for creating/updating dynamic membership rules */
export type DynamicMembershipRulePayload = {
  ruleType: `${RuleType}`;
  attribute?: string;
  personaValues: string[];
  operator?: GroupRuleOperator;
};
export type GroupProps = Overwrite<OptionalGroupProps, {
  dynamicMemberships?: GroupDynamicMembershipWithRuleType[];
}>;
export interface ExtendedGroupDynamicMembership extends GroupDynamicMembershipProps {
  ruleType?: `${RuleType}`;
  worksites?: WorksiteProps[];
}
export interface ExtendedGroupProps extends GroupProps {
  isDynamic: boolean;
  isAllEmployees: boolean;
  scimControlled: boolean;
  dynamicMemberships?: ExtendedGroupDynamicMembership[];
}
export const GroupFormatter = {
  toExtendedGroupFormat(group: GroupProps, worksites?: WorksiteProps[]): ExtendedGroupProps {
    return {
      ...group,
      isDynamic: Boolean(group.dynamicMemberships?.length),
      isAllEmployees: (group.dynamicMemberships || []).filter(rule => rule.personaPath === '*' && Array.isArray(rule.personaValues) && rule.personaValues.filter(value => value === '*').length > 0).length > 0,
      scimControlled: Boolean(group.scimControlled),
      dynamicMemberships: (group.dynamicMemberships || []).map(rule => {
        const {
          ruleType
        } = rule;
        return {
          ...rule,
          ruleType,
          operator: rule.operator ?? GroupRuleOperatorValue.Is,
          worksites: ruleType === RuleType.LOCATION ? (worksites || []).filter(worksite => indexOf(rule.personaValues, worksite.id) > -1) : undefined
        };
      })
    };
  }
};

/**
 * LEGACY
 */
export const legacyEnterpriseGroupUri = '/_/enterprises/:enterpriseId/groups';
export const fetchGroups = (entityId: string, queryParams: Record<string, any>) => apiDashboardServer(legacyEnterpriseGroupUri.replace(':enterpriseId', entityId), {
  method: 'GET',
  queryParams
});

/*
 * GET METHODS
 */
export const fetchEnterpriseGroups = (enterpriseId: string, queryParams: RequestParams & {
  '%:name'?: string;
  'enterpriseGroupPlans.planId'?: string;
}): Promise<GetResponse<GroupProps>> => apiDashboardServer(endpoints.GET.EnterpriseGroups.route(enterpriseId), {
  method: 'get',
  queryParams
});
export function fetchOneGroup(groupId: string, queryParams: RequestParams): Promise<GroupProps> {
  return apiDashboardServer(endpoints.GET.Group.route(groupId), {
    method: 'get',
    queryParams
  });
}
export const getPlansLinkedToGroup = async (groupId: string, queryParams?: RequestParams): Promise<GetResponse<PlanProps>> => {
  const res = await apiDashboardServer(endpoints.GET.GroupPlans.route(groupId), {
    method: 'get',
    queryParams
  });
  res.records.forEach(plan => captureIsPlanValid(plan, {
    endpoint: endpoints.GET.GroupPlans.route(groupId),
    queryParams
  }));
  return res;
};

/*
 * POST METHODS
 */
export type GroupWriteBody = {
  name: string;
  enterpriseId: string;
  dynamicMemberships?: DynamicMembershipRulePayload[];
  staticMemberships?: {
    accountId: string;
  }[];
};
export const postGroup = (body: GroupWriteBody): Promise<GroupProps> => apiDashboardServer(endpoints.POST.Group.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(body)
});
export const postGroupSimulate = (body: GroupWriteBody): Promise<GetResponse<Account>> => apiDashboardServer(endpoints.POST.GroupSimulateRules.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(body)
});
export interface GroupPlan {
  planId: string;
  enterpriseGroupId: string;
}
export const postEnterpriseGroupsPlans = (body: {
  groupId: string;
  planIds: string[];
}[]): Promise<GroupPlan> => apiDashboardServer(endpoints.POST.EnterpriseGroupsPlans.route(), {
  method: 'post',
  data: body
});
export const putGroup = (groupId: string, body: Partial<GroupWriteBody>): Promise<GroupProps> => apiDashboardServer(endpoints.PUT.Group.route(groupId), {
  method: 'put',
  headers: JSON_HEADER,
  data: JSON.stringify(body)
});
export const useUpdateGroup = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};

/*
 * DELETE METHOD
 */
export const deleteGroup = (groupId: string): Promise<void> => apiDashboardServer(endpoints.DELETE.Group.route(groupId), {
  method: 'delete'
});
function _temp(t0) {
  const {
    groupId,
    group
  } = t0;
  return putGroup(groupId, group);
}